import { useIsMobile } from "../../hooks/useIsMobile";
import { NAVY_ACTION } from "./shared";
import ReviewChecklist from "./ReviewChecklist";
import { reviewItemsFor, reviewChecklistState, statusBlockReason } from "../../config/contractReviewItems";

const STATUS_OPTIONS = ["Pending", "Approved", "Approved with Comments", "Rejected"];

// checklist (layout 3): { items: answers[], optionRows, canAnswer(row), onAnswer, onLogIssue, busy }
// — when present, the Responsibility column becomes the per-item checklist and
// the Status choices are gated by it (contractReviewItems.js).
export default function ContractReviewPanel({ rows, onSave, onUpdate, savingId, currentUser, onSendReview, sending, directoryUsers, checklist = null }) {
  const isMobile = useIsMobile();
  const itemsOf = (row) => (checklist ? reviewItemsFor(row, checklist.optionRows, checklist.items) : []);
  const stateOf = (row) => reviewChecklistState(itemsOf(row));
  const responsibilityCell = (row) => {
    const items = itemsOf(row);
    if (!checklist || items.length === 0) return row.Responsibility;
    return <ReviewChecklist review={row} items={items} canAnswer={checklist.canAnswer(row)} onAnswer={checklist.onAnswer} onLogIssue={checklist.onLogIssue} busy={checklist.busy} compact={isMobile} />;
  };
  const statusOptions = (row) => STATUS_OPTIONS.map(st => {
    const why = statusBlockReason(st, stateOf(row));
    return <option key={st} value={st} disabled={!!why && row.Status !== st} title={why || ""}>{st}{why && row.Status !== st ? " — answer the checklist first" : ""}</option>;
  });
  const STATUS_COLORS  = {
    "Pending":                  { bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb" },
    "Approved":                 { bg: "#d1fae5", color: "#065f46", border: "#6ee7b7" },
    "Approved with Comments":   { bg: "#fef3c7", color: "#92400e", border: "#fcd34d" },
    "Rejected":                 { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" },
  };

  const allDone    = rows.length > 0 && rows.every(r => r.Status === "Approved" || r.Status === "Approved with Comments");
  const hasReject  = rows.some(r => r.Status === "Rejected");
  const pendingCnt = rows.filter(r => r.Status === "Pending").length;
  const allHaveEmail = rows.length > 0 && rows.every(r => r.AssignedEmail && r.AssignedEmail.trim());

  const cols = checklist ? "130px 340px 170px 1fr 150px" : "140px 220px 180px 1fr 130px";
  const inputStyle = {
    width: "100%", padding: "5px 8px", fontSize: isMobile ? 16 : 12,
    border: "1px solid var(--cs-border)", borderRadius: 5,
    background: "var(--surface)", color: "var(--text)",
    fontFamily: "inherit",
  };

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Summary bar — sticky so "Send for Review" stays reachable while scrolling */}
      <div style={{
        display: "flex", flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center",
        gap: isMobile ? 8 : 16, marginBottom: 12,
        padding: "10px 14px", background: "#f8f9fb", border: "1px solid var(--cs-border)", borderRadius: 8,
        position: isMobile ? "static" : "sticky", top: 0, zIndex: 6,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}>
        <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
          Contract Review Sign-Off Matrix
        </div>
        {hasReject && <span style={{ fontSize: 12, fontWeight: 700, color: "#dc2626", background: "#fee2e2", padding: "3px 10px", borderRadius: 100, alignSelf: isMobile ? "flex-start" : "center" }}>⚠ Rejection — review required</span>}
        {!hasReject && allDone && <span style={{ fontSize: 12, fontWeight: 700, color: "#065f46", background: "#d1fae5", padding: "3px 10px", borderRadius: 100, alignSelf: isMobile ? "flex-start" : "center" }}>✓ All departments signed off</span>}
        {!hasReject && !allDone && <span style={{ fontSize: 12, color: "var(--text3)" }}>{pendingCnt} of {rows.length} pending</span>}
        <button
          onClick={onSendReview}
          disabled={!allHaveEmail || sending}
          title={!allHaveEmail ? "All assigned contacts must have an email address before sending" : "Send review request to all listed contacts"}
          style={{
            padding: "6px 16px", fontSize: 12, fontWeight: 700,
            background: allHaveEmail ? NAVY_ACTION : "#e5e7eb",
            color: allHaveEmail ? "#fff" : "#9ca3af",
            border: "none", borderRadius: 6,
            cursor: allHaveEmail && !sending ? "pointer" : "not-allowed",
            whiteSpace: "nowrap", flexShrink: 0,
            width: isMobile ? "100%" : "auto",
          }}
        >
          {sending ? "Sending…" : "✉ Send for Review"}
        </button>
      </div>

      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map(row => {
            const sc = STATUS_COLORS[row.Status] || STATUS_COLORS["Pending"];
            const isSaving = savingId === row.ReviewID;
            return (
              <div key={row.ReviewID} style={{
                border: `1px solid ${sc.border}`, borderRadius: 10,
                background: "var(--surface)", padding: 12,
                opacity: isSaving ? 0.6 : 1,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 4 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>{row.Department}</div>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 100, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, flexShrink: 0 }}>
                    {row.Status}
                  </span>
                </div>
                {row.ReviewerName && (
                  <div style={{ fontSize: 10, color: "var(--text3)", marginBottom: 6 }}>
                    Signed: {row.ReviewerName}
                    {row.ReviewedAt && " · " + new Date(row.ReviewedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </div>
                )}
                <div style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.4, marginBottom: 10 }}>{responsibilityCell(row)}</div>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>Assigned To</div>
                  <select
                    value={row.AssignedTo || ""}
                    onChange={e => {
                      const picked = directoryUsers?.find(u => u.label === e.target.value);
                      const name  = picked?.label || e.target.value;
                      const email = picked?.email || "";
                      onUpdate(row.ReviewID, "AssignedTo", name);
                      onUpdate(row.ReviewID, "AssignedEmail", email);
                      onSave({ ...row, AssignedTo: name, AssignedEmail: email });
                    }}
                    style={{ ...inputStyle, cursor: "pointer" }}
                  >
                    <option value="">— Select —</option>
                    {row.AssignedTo && !(directoryUsers || []).some(u => u.label === row.AssignedTo) && (
                      <option value={row.AssignedTo}>{row.AssignedTo} (not in directory)</option>
                    )}
                    {(directoryUsers || []).map(u => (
                      <option key={u.email} value={u.label}>{u.label}</option>
                    ))}
                  </select>
                  {row.AssignedEmail && (
                    <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 3 }}>{row.AssignedEmail}</div>
                  )}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>Comments</div>
                  <input
                    defaultValue={row.Comments || ""}
                    placeholder="Add comments…"
                    style={inputStyle}
                    onBlur={e => { onUpdate(row.ReviewID, "Comments", e.target.value); onSave({ ...row, Comments: e.target.value }); }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>Status</div>
                  <select
                    value={row.Status}
                    onChange={e => {
                      const newStatus = e.target.value;
                      const now = newStatus !== "Pending" ? new Date().toISOString() : null;
                      const reviewer = newStatus !== "Pending" ? (currentUser?.name || "") : null;
                      const reviewerEmail = newStatus !== "Pending" ? (currentUser?.email || "") : null;
                      onUpdate(row.ReviewID, "Status", newStatus);
                      onSave({ ...row, Status: newStatus, ReviewerName: reviewer, ReviewerEmail: reviewerEmail, ReviewedAt: now });
                    }}
                    style={{ ...inputStyle, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, fontWeight: 600, cursor: "pointer" }}
                  >
                    {statusOptions(row)}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ border: "1px solid var(--cs-border)", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: cols, background: "#f8f9fb", borderBottom: "2px solid var(--cs-border)" }}>
            {["Department", checklist ? "Checklist — answer every item" : "Responsibility", "Assigned To", "Comments", "Status"].map(h => (
              <div key={h} style={{ padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
            ))}
          </div>
          {rows.map((row, i) => {
            const sc = STATUS_COLORS[row.Status] || STATUS_COLORS["Pending"];
            const isSaving = savingId === row.ReviewID;
            return (
              <div key={row.ReviewID} style={{
                display: "grid", gridTemplateColumns: cols,
                borderBottom: i < rows.length - 1 ? "1px solid var(--cs-border)" : "none",
                background: i % 2 === 0 ? "transparent" : "#fafbfc",
                opacity: isSaving ? 0.6 : 1,
                alignItems: "start",
              }}>
                <div style={{ padding: "10px 12px" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{row.Department}</div>
                  {row.ReviewerName && (
                    <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}>
                      Signed: {row.ReviewerName}<br />
                      {row.ReviewedAt && new Date(row.ReviewedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </div>
                  )}
                </div>
                <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--text2)", lineHeight: 1.4 }}>{responsibilityCell(row)}</div>
                <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: 2 }}>
                  <select
                    value={row.AssignedTo || ""}
                    onChange={e => {
                      const picked = directoryUsers?.find(u => u.label === e.target.value);
                      const name  = picked?.label || e.target.value;
                      const email = picked?.email || "";
                      onUpdate(row.ReviewID, "AssignedTo", name);
                      onUpdate(row.ReviewID, "AssignedEmail", email);
                      onSave({ ...row, AssignedTo: name, AssignedEmail: email });
                    }}
                    style={{ ...inputStyle, cursor: "pointer" }}
                  >
                    <option value="">— Select —</option>
                    {row.AssignedTo && !(directoryUsers || []).some(u => u.label === row.AssignedTo) && (
                      <option value={row.AssignedTo}>{row.AssignedTo} (not in directory)</option>
                    )}
                    {(directoryUsers || []).map(u => (
                      <option key={u.email} value={u.label}>{u.label}</option>
                    ))}
                  </select>
                  {row.AssignedEmail && <div style={{ fontSize: 11, color: "var(--text3)" }}>{row.AssignedEmail}</div>}
                </div>
                <div style={{ padding: "8px 10px" }}>
                  <input
                    defaultValue={row.Comments || ""}
                    placeholder="Add comments…"
                    style={inputStyle}
                    onBlur={e => { onUpdate(row.ReviewID, "Comments", e.target.value); onSave({ ...row, Comments: e.target.value }); }}
                  />
                </div>
                <div style={{ padding: "8px 10px" }}>
                  <select
                    value={row.Status}
                    onChange={e => {
                      const newStatus = e.target.value;
                      const now = newStatus !== "Pending" ? new Date().toISOString() : null;
                      const reviewer = newStatus !== "Pending" ? (currentUser?.name || "") : null;
                      const reviewerEmail = newStatus !== "Pending" ? (currentUser?.email || "") : null;
                      onUpdate(row.ReviewID, "Status", newStatus);
                      onSave({ ...row, Status: newStatus, ReviewerName: reviewer, ReviewerEmail: reviewerEmail, ReviewedAt: now });
                    }}
                    style={{ ...inputStyle, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, fontWeight: 600, cursor: "pointer" }}
                  >
                    {statusOptions(row)}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
